import { useEffect, useState, type FunctionComponent } from 'react';
import { useFlags, withLDConsumer, type LDClient } from 'launchdarkly-react-client-sdk';
import { useMediaQuery } from 'react-responsive';
import { showErrorToast, showSuccessToast, Toast } from '@fff-web/fff-ui-shared';
import { useUserContext } from '@fff-web/fff-utilities';
import Layout from '../../components/Layout';
import useLaunchDarkly from '../../hooks/useLaunchDarkly';
import FormButtons from './FormButtons';
import Header from './Header';
import { isFormDisabled } from './helpers';
import useEmailPreferences from './hooks/useEmailPreferences';
import OptionList from './OptionList';
import { options } from './options';
import './styles.css';

type EmailPreferencesProps = {
  ldClient: LDClient;
};

const EmailPreferences: FunctionComponent<EmailPreferencesProps> = ({ ldClient }) => {
  const [step, setStep] = useState(0);
  const [currentPreference, setCurrentPreference] = useState(null);
  const [emailPreferences, updatePreference] = useEmailPreferences();
  const { userInfo } = useUserContext();
  useLaunchDarkly(ldClient, userInfo);
  const { chatbotAppliedLab: showChatbot } = useFlags();
  const isMobile = useMediaQuery({
    query: '(max-width: 767px)',
  });
  let noteStyle = step === 0 ? { margin: '4px 0 30px 0' } : { margin: '24px 0 36px 0' };
  noteStyle = isMobile ? noteStyle : {};
  const lastDividerStyle = step === 0 ? { margin: '24px 0' } : { margin: '12px 0 24px' };
  const cancelSubscriptionChange = () => {
    setCurrentPreference(emailPreferences.data.optionEmailPreferences);
    setStep(0);
  };
  const nextStep = () => setStep(1);
  const editOrCancel = step === 0 ? nextStep : cancelSubscriptionChange;
  const savePreference = () => {
    updatePreference(currentPreference)
      .then(() => {
        showSuccessToast(
          'Email Preferences Updated',
          'Your notifications change has been successfully saved.',
        );
        setStep(0);
      })
      .catch((_: unknown) => {
        showErrorToast('Email Preferences not Updated', 'Ops! Something went wrong...');
        setStep(0);
      });
  };

  useEffect(() => {
    const { data } = emailPreferences;
    if (data && data.optionEmailPreferences) {
      setCurrentPreference(data.optionEmailPreferences);
    }
  }, [emailPreferences]);

  return (
    <>
      <Layout isLoading={false} redirectIfNoAuth={false} hideChatWidget={!showChatbot}>
        <div className="layout-container">
          <div className="preferences-container" data-testid="email-preferences-container">
            <Header currentStep={step} onActionClick={editOrCancel} />
            <div className="preferences-body" data-testid="email-preferences-body">
              {step === 0 && (
                <>
                  {Object.keys(emailPreferences.data).length > 0 &&
                    emailPreferences.data.optionEmailPreferences && (
                      <h3 className="preferences-body-title">
                        {options[emailPreferences.data.optionEmailPreferences - 1]?.label}
                      </h3>
                    )}
                </>
              )}
              {step === 1 && (
                <OptionList
                  selectOption={(option) => setCurrentPreference(option)}
                  currentPreference={currentPreference}
                />
              )}
              <p className="note" style={noteStyle}>
                Please Note: Managing your preferences does not impact transactional emails such as
                payment invoices, upcoming billing, shipping or delivery notifications. You will
                continue to receive these emails.
              </p>
            </div>
            {step === 1 && (
              <FormButtons
                onSave={savePreference}
                onCancel={cancelSubscriptionChange}
                disabled={isFormDisabled(emailPreferences, currentPreference)}
              />
            )}
            <div id="last" className="divider" style={lastDividerStyle} />
          </div>
        </div>
      </Layout>
      <Toast />
    </>
  );
};

export default withLDConsumer()(EmailPreferences);
